import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import ForgeShell, { ControlLabel, Chips, OutputCard, EmptyState } from "./ForgeShell";
import { ArrowRight, Plus, Power, PowerOff, GitBranch, Loader2, Pencil, Check, X, GripVertical } from "lucide-react";
import { toast } from "sonner";

const FUNNEL_TEMPLATES = [
  { value: "lead_capture", label: "Lead capture",     nodes: ["Reel", "Comment", "DM", "Offer", "Checkout"] },
  { value: "dm_flow",      label: "DM flow",          nodes: ["Reply", "Qualify", "AI close", "Payment"] },
  { value: "webinar",      label: "Webinar",          nodes: ["Ad", "Register", "Live", "Replay", "Offer", "Payment"] },
  { value: "checkout",     label: "Checkout",         nodes: ["Landing", "Cart", "Payment", "Confirm"] },
  { value: "recovery",     label: "Recovery",         nodes: ["Abandoned", "DM nudge", "Email", "Retry", "Recover"] },
];

// Node palette — clicking adds a step into the active flow.
const NODE_PALETTE = [
  "Reel", "Comment", "Story view", "DM", "AI DM", "AI reply",
  "Qualify", "Register", "Attend", "Replay", "Landing",
  "Offer", "Cart", "Checkout", "Payment", "Confirm",
  "Upsell", "Abandoned", "Recover", "Email",
];

function StatusDot({ active }) {
  return <span className={`pulse-dot ${active ? "" : "red"}`} />;
}

function FlowNode({ label, idx, editing, onEdit, onCommit, onDelete, dragHandleProps }) {
  const [v, setV] = useState(label);
  useEffect(() => { setV(label); }, [label]);

  return (
    <div className={`relative rounded-2xl border ${editing ? "border-ai bg-ai-soft" : "border-line bg-white"} px-3 py-2.5 min-w-[110px] max-w-[160px] shadow-sm`} data-testid={`flow-node-${idx}`}>
      <div className="flex items-center justify-between gap-1">
        <p className="text-[10px] uppercase tracking-wider text-meta font-bold">Step {idx + 1}</p>
        {dragHandleProps && (
          <button
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing opacity-40 hover:opacity-100 transition"
            data-testid={`flow-node-drag-${idx}`}
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-3.5 w-3.5 text-meta" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1">
          <input value={v} onChange={(e) => setV(e.target.value)} autoFocus
            className="flex-1 min-w-0 bg-transparent text-sm font-semibold text-ink outline-none" data-testid={`flow-node-input-${idx}`} />
          <button onClick={() => onCommit(v)} className="text-success p-0.5" data-testid={`flow-node-save-${idx}`}><Check className="h-3.5 w-3.5" /></button>
          <button onClick={() => onCommit(null)} className="text-meta p-0.5"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-1.5 mt-0.5">
          <p className="text-sm font-semibold text-ink truncate flex-1">{label}</p>
          <button onClick={onEdit} className="opacity-50 hover:opacity-100 transition text-meta" data-testid={`flow-node-edit-${idx}`}>
            <Pencil className="h-3 w-3" />
          </button>
        </div>
      )}
      {onDelete && (
        <button onClick={onDelete} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-zinc-200 text-meta hover:bg-rose-100 hover:text-danger text-[10px] grid place-items-center transition" data-testid={`flow-node-delete-${idx}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function FlowChain({ flow, onPersist }) {
  const [nodes, setNodes] = useState(flow.steps || []);
  const [editingIdx, setEditingIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dirty = JSON.stringify(nodes) !== JSON.stringify(flow.steps || []);

  useEffect(() => { setNodes(flow.steps || []); }, [flow.id, flow.steps]);

  const commit = (i, val) => {
    if (val === null) { setEditingIdx(null); return; }
    const v = (val || "").trim() || nodes[i];
    const next = [...nodes];
    next[i] = v;
    setNodes(next);
    setEditingIdx(null);
  };
  const add = (label = "New step") => setNodes([...nodes, label]);
  const del = (i) => setNodes(nodes.filter((_, idx) => idx !== i));

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* noop */ }
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    if (overIdx !== i) setOverIdx(i);
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    if (from === null || from === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...nodes];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setNodes(next);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const save = async () => {
    setBusy(true);
    try {
      await onPersist(nodes);
      toast.success("Flow saved");
    } catch {
      toast.error("Could not save flow");
    }
    setBusy(false);
  };

  return (
    <div className="surface p-5 space-y-3" data-testid={`flow-${flow.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot active={flow.active !== false} />
          <h3 className="font-display font-bold text-ink truncate">{flow.name}</h3>
          <span className={`pill ${flow.active !== false ? "pill-success" : "pill-neutral"} shrink-0`}>
            {flow.active !== false ? <><Power className="h-3 w-3" /> Live</> : <><PowerOff className="h-3 w-3" /> Paused</>}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-meta">
          <span><span className="font-bold text-ink">{Math.round((flow.conversion || 0) * 100)}%</span> conversion</span>
          <span>· <span className="font-bold text-success">${Math.round(flow.revenue_30d || 0).toLocaleString()}</span> / 30d</span>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-2">
        {nodes.map((label, i) => (
          <React.Fragment key={`${flow.id}-${i}`}>
            <div
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDrop={onDrop(i)}
              onDragEnd={onDragEnd}
              className={`shrink-0 transition ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-ai rounded-2xl" : ""} ${dragIdx === i ? "opacity-40" : ""}`}
              data-testid={`flow-node-wrap-${i}`}
            >
              <FlowNode
                label={label}
                idx={i}
                editing={editingIdx === i}
                onEdit={() => setEditingIdx(i)}
                onCommit={(v) => commit(i, v)}
                onDelete={() => del(i)}
                dragHandleProps={{}}
              />
            </div>
            {i < nodes.length - 1 && <ArrowRight className="h-4 w-4 text-meta-soft shrink-0" />}
          </React.Fragment>
        ))}
        <button onClick={() => add()} className="rounded-xl border border-dashed border-line text-meta hover:border-ai hover:text-ai px-3 py-2.5 text-xs font-semibold inline-flex items-center gap-1 shrink-0 transition" data-testid={`flow-node-add-${flow.id}`}>
          <Plus className="h-3 w-3" /> Step
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <details className="text-xs text-meta">
          <summary className="cursor-pointer hover:text-ai" data-testid={`flow-palette-toggle-${flow.id}`}>+ Add from palette</summary>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`flow-palette-${flow.id}`}>
            {NODE_PALETTE.map((p) => (
              <button
                key={p}
                onClick={() => add(p)}
                className="rounded-full bg-white border border-line px-2.5 py-1 text-[11px] font-medium text-ink-soft hover:border-ai hover:text-ai transition"
                data-testid={`flow-palette-add-${flow.id}-${p}`}
              >
                + {p}
              </button>
            ))}
          </div>
        </details>

        {dirty && (
          <button onClick={save} disabled={busy} data-testid={`flow-save-${flow.id}`}
            className="rounded-full ashra-gradient-bg px-4 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-60">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save changes
          </button>
        )}
      </div>
    </div>
  );
}

export default function FunnelLab() {
  const [flows, setFlows] = useState(null);
  const [tplKind, setTplKind] = useState("lead_capture");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/flows");
      setFlows(r.data || []);
    } catch {
      setFlows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const createFromTemplate = async () => {
    const t = FUNNEL_TEMPLATES.find(f => f.value === tplKind);
    if (!t) return;
    setCreating(true);
    try {
      await api.post("/flows", { name: t.label, steps: t.nodes });
      toast.success(`${t.label} flow created`);
      await load();
    } catch (e) {
      // Fallback if /flows POST isn't wired — surface a friendly notice
      toast.error(e?.response?.data?.detail || "Flow creation endpoint pending — view existing flows");
    }
    setCreating(false);
  };

  const persistSteps = async (flowId, steps) => {
    try {
      await api.patch(`/flows/${flowId}`, { steps });
    } catch (e) {
      // If PATCH not implemented yet, persist locally
      setFlows(prev => (prev || []).map(f => f.id === flowId ? { ...f, steps } : f));
      throw e;
    }
    await load();
  };

  return (
    <ForgeShell
      testid="forge-funnel-lab"
      left={
        <>
          <ControlLabel hint="Pick a starting shape">Funnel template</ControlLabel>
          <Chips testid="funnel-template" options={FUNNEL_TEMPLATES} value={tplKind} onChange={setTplKind} />

          <button onClick={createFromTemplate} disabled={creating} data-testid="funnel-create"
            className="w-full rounded-full ashra-gradient-bg px-4 py-3 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 shadow-sm disabled:opacity-60 focus-ring">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {creating ? "Creating…" : "Create funnel"}
          </button>

          <div className="rounded-xl border border-line bg-zinc-50 p-3 text-[11px] text-meta-soft">
            Each node is editable. Conversion % and revenue are computed from real flow data.
          </div>
        </>
      }
      center={
        flows === null ? (
          <div className="surface p-10 text-center text-meta inline-flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading flows…</div>
        ) : flows.length === 0 ? (
          <EmptyState title="No flows yet" body="Create one from a template to start visualising your revenue chain." />
        ) : (
          <>
            {flows.map(f => <FlowChain key={f.id} flow={f} onPersist={(steps) => persistSteps(f.id, steps)} />)}
          </>
        )
      }
      right={
        <>
          <div>
            <ControlLabel>Operational health</ControlLabel>
            <div className="mt-2 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-meta">Live flows</span>
                <span className="text-ink font-bold">{(flows || []).filter(f => f.active !== false).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-meta">30d revenue</span>
                <span className="text-success font-bold">${Math.round((flows || []).reduce((a, f) => a + (f.revenue_30d || 0), 0)).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-meta">Avg conversion</span>
                <span className="text-ai font-bold">
                  {flows && flows.length > 0 ? Math.round(flows.reduce((a, f) => a + (f.conversion || 0), 0) / flows.length * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200/60 p-3 text-[11px] text-ink-soft">
            <GitBranch className="h-3.5 w-3.5 text-ai inline mr-1" />
            Connect nodes to Capture, Convert and Recover by saving — your Workforce picks them up automatically.
          </div>
        </>
      }
    />
  );
}
